"use client";

import React from "react";
import { FileText, BookOpen, Video, Presentation, FileCheck, ArrowRight, Sparkles, Compass } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const resourceCategories = [{
  id: "articles",
  title: "Articles & Insights",
  tagline: "Industry Analysis",
  description: "Analytical method development tips, regulatory guidance, and best practices for compliant laboratories.",
  icon: FileText,
  count: "48+ Articles",
  highlight: "Updated Weekly",
  featuredTopic: "Method Transfer in Regulated QC Labs",
  link: "/ResourcesPage"
}, {
  id: "case-studies",
  title: "Case Studies",
  tagline: "Proven Implementations",
  description: "Real-world installations detailing reduced cycle times, blend uniformity validation, and raw material verification.",
  icon: BookOpen,
  count: "24+ Studies",
  highlight: "Pharma & Biotech",
  featuredTopic: "Real-Time NIR Blend Monitoring Success",
  link: "/ResourcesPage"
}, {
  id: "technical-videos",
  title: "Technical Videos",
  tagline: "Instrument Demonstrations",
  description: "Step-by-step instrument workflows, hardware calibrations, software overviews, and maintenance walkthroughs.",
  icon: Video,
  count: "35+ Videos",
  highlight: "HD Walkthroughs",
  featuredTopic: "Handheld Raman Raw Material Screening",
  link: "/ResourcesPage"
}, {
  id: "webinars",
  title: "Webinars & Workshops",
  tagline: "Expert-Led Sessions",
  description: "Interactive technical presentations covering Process Analytical Technology (PAT) and chemometric model development.",
  icon: Presentation,
  count: "18+ Sessions",
  highlight: "On-Demand & Live",
  featuredTopic: "Chemometrics for In-Line Spectroscopy",
  link: "/ResourcesPage"
}, {
  id: "whitepapers",
  title: "Whitepapers & Guides",
  tagline: "In-Depth Research",
  description: "Comprehensive technical reports on optical spectroscopy, powder characterisation standards, and qualification protocols.",
  icon: FileCheck,
  count: "16+ Reports",
  highlight: "Peer-Reviewed",
  featuredTopic: "EP & USP Powder Flow Testing Guidelines",
  link: "/ResourcesPage"
}];
export default function ResourceTypes() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.21, 0.45, 0.27, 0.9]
      }
    }
  };
  return <section id="resourcetypesgrid" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="page-container relative z-10">
        {/* Header Section */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 gap-1.5 text-xs font-semibold">
              <Compass className="size-3.5 text-primary" />
              Knowledge Hub
            </Badge>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Antech Solutions Library
            </span>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight mb-4">
            Explore our technical resources and application knowledge
          </h2>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Access technical articles, peer-reviewed whitepapers, application case studies,
            and video tutorials curated by our analytical chemistry and instrumentation specialists.
          </p>
        </div>

        {/* Resources Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }}>
          {resourceCategories.map((resource, i) => {
          const IconComponent = resource.icon;
          const isWideCard = i === 3 || i === 4;
          return <motion.div key={resource.id} data-index={i} variants={itemVariants} className={isWideCard ? "lg:col-span-1.5" : "lg:col-span-1"}>
                <Card className="h-full flex flex-col justify-between border border-border/80 hover:border-primary/40 bg-card hover:shadow-md transition-all duration-200">
                  <CardHeader className="space-y-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="size-12 rounded-lg bg-secondary flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge variant="outline" className="text-xs bg-background/50 font-medium">
                        {resource.count}
                      </Badge>
                    </div>

                    <div>
                      <span className="text-xs font-medium text-primary tracking-wide uppercase">
                        {resource.tagline}
                      </span>
                      <CardTitle className="text-xl md:text-2xl font-bold mt-1 text-foreground">
                        {resource.title}
                      </CardTitle>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <CardDescription className="text-sm text-muted-foreground leading-normal">
                      {resource.description}
                    </CardDescription>

                    <div className="p-3 rounded-md bg-secondary/50 border border-border/50 text-xs text-secondary-foreground space-y-1">
                      <div className="flex items-center gap-1.5 font-semibold text-foreground">
                        <Sparkles className="size-3 text-primary shrink-0" />
                        <span>Featured Topic:</span>
                      </div>
                      <p className="text-muted-foreground line-clamp-1">{resource.featuredTopic}</p>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-2">
                    <Button asChild variant="outline" className="w-full justify-between group hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
                      <Link to={resource.link}>
                        <span>Browse {resource.title.split(" ")[0]}</span>
                        <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Callout Banner */}
        <div className="mt-12 md:mt-16 rounded-xl border border-border bg-secondary/60 p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="max-w-xl">
            <h3 className="text-lg md:text-xl font-bold text-foreground">
              Need a specific application note or custom feasibility report?
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Our application support team in Ireland and the UK can run sample feasibility tests and build tailored chemometric models.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <Button asChild variant="default">
              <Link to="/DiscussYourApplicationPage">
                Discuss Your Application
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/BookADemoPage">
                Book a Demo
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}